/**
 * /w/:wsId/p/:pKey/settings — board settings (FRIDAY-43, FRIDAY-65).
 *
 * The screen is a tabbed shell, like the workspace settings. The tabs are
 * ROUTES, so the URL is the source of truth:
 *
 *   /w/:wsId/p/:pKey/settings              → Board (default)
 *   /w/:wsId/p/:pKey/settings/members      → The members allowed in this board
 *   /w/:wsId/p/:pKey/settings/labels       → This board's labels
 *   /w/:wsId/p/:pKey/settings/milestones   → This board's milestones
 *
 * The Board tab owns three independent sections:
 *
 *   A. Board identity — display name, colour, icon. The board KEY is
 *      immutable (it is part of every task key), so it is shown read-only.
 *   B. Board columns — the board tier of the shared list defaults. It can
 *      inherit the workspace default, override it, or be reset to inherit.
 *   C. Danger area — archiving the board. It lives here (and not in the
 *      board's kebab menu) so the confirmation can name the board and point
 *      at the restore path.
 *
 * Labels and milestones are board-owned, but they are managed from one
 * place: the workspace settings list them for every board, the board
 * settings show the ones of this board.
 */

import { useEffect, useRef, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate, useParams } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '@/lib/api'
import { useAuth } from '@/features/auth/authContext'
import { toast } from '@/features/feedback/toast'
import { useProjectByKey } from '@/features/projects/board'
import { ArchiveBoardDialog } from '@/features/projects/ArchiveBoardDialog'
import { useWorkspaceMembers } from '@/features/workspaces/workspaces'
import {
  useBoardListDefaults,
  useSaveBoardListDefaults,
  useUpdateProjectIdentity,
  type ProjectIdentityPatch,
} from '@/features/settings/listDefaults'
import { useListDefaultsDraft } from '@/features/settings/useListDefaultsDraft'
import { ListFieldsEditor } from '@/components/settings/ListFieldsEditor'
import { Tabs } from '@/components/ui/Tabs'
import { BoardMembersPanel } from '@/components/workspace/BoardMembersPanel'
import { WorkspaceLabelsPanel } from '@/components/workspace/WorkspaceLabelsPanel'
import { WorkspaceMilestonesPanel } from '@/components/workspace/WorkspaceMilestonesPanel'
import { CloseSettingsButton } from '@/components/settings/CloseSettingsButton'
import { MoveToWorkspaceDialog } from '@/components/workspace/MoveToWorkspaceDialog'
import {
  NavigationGuardProvider,
  useUnsavedChangesGuard,
} from '@/features/settings/useUnsavedChangesGuard'
import { DEFAULT_LIST_FIELDS } from '@friday/shared/listFields'
import type { Project, ProjectMember } from '@friday/shared/realtime'

/** The colour a board falls back to — the server rejects an empty colour. */
const DEFAULT_BOARD_COLOR = '#5B5BD6'
const PRESET_COLORS = ['#5B5BD6', '#0F766E', '#B45309', '#BE123C', '#1D4ED8', '#7C3AED']

interface Identity {
  name: string
  color: string
  icon: string
}


/**
 * The tab strip. `to` is the route the tab lives at — adding a tab is one
 * entry here plus one route in `routes.tsx`.
 */
export const BOARD_SETTINGS_TABS = [
  { id: 'board', label: 'Board', to: '/w/$wsId/p/$pKey/settings' },
  { id: 'members', label: 'Members', to: '/w/$wsId/p/$pKey/settings/members' },
  { id: 'labels', label: 'Labels', to: '/w/$wsId/p/$pKey/settings/labels' },
  { id: 'milestones', label: 'Milestones', to: '/w/$wsId/p/$pKey/settings/milestones' },
] as const

const BOARD_TABS_ID_BASE = 'board-settings'

/** The layout route of the board settings area. It owns the tab strip. */
export function BoardSettingsLayout() {
  const { wsId, pKey } = useParams({ strict: false }) as { wsId: string; pKey: string }
  const navigate = useNavigate()
  const location = useLocation()
  const activeId = activeBoardTabId(location.pathname, wsId, pKey)

  return (
    <div className="flex h-full flex-col">
      <div className="border-surface-200 dark:border-surface-700 flex items-start justify-between gap-3 border-b px-4 pt-3 sm:px-6">
        <Tabs
          tabs={BOARD_SETTINGS_TABS.map((t) => ({ id: t.id, label: t.label }))}
          activeId={activeId}
          label="Board settings sections"
          idBase={BOARD_TABS_ID_BASE}
          onSelect={(id) => {
            const tab = BOARD_SETTINGS_TABS.find((t) => t.id === id)
            if (!tab) return
            navigate({ to: tab.to, params: { wsId, pKey } })
          }}
        />
        <CloseSettingsButton fallback={`/w/${wsId}/p/${pKey}`} />
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        <Outlet />
      </div>
    </div>
  )
}

/** Which tab the current URL selects; unknown suffixes fall back to Board. */
function activeBoardTabId(pathname: string, wsId: string, pKey: string): string {
  const prefix = `/w/${wsId}/p/${pKey}/settings`
  const rest = pathname.startsWith(prefix)
    ? pathname.slice(prefix.length).replace(/^\//, '').replace(/\/+$/, '')
    : ''
  const match = BOARD_SETTINGS_TABS.find(
    (t) => t.to === `/w/$wsId/p/$pKey/settings${rest ? `/${rest}` : ''}`,
  )
  return match ? match.id : 'board'
}

/** Members tab — the members allowed in this board. */
export function BoardSettingsMembersView() {
  const { wsId, pKey } = useParams({ strict: false }) as { wsId: string; pKey: string }
  const projectQ = useProjectByKey(wsId, pKey)
  const project = projectQ.data

  if (projectQ.isLoading || !project) {
    return (
      <div className="text-surface-500 flex h-full items-center justify-center text-sm">
        Loading board…
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto">
      <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
        <header>
          <h1 className="text-lg font-semibold">Members</h1>
          <p className="text-surface-500 mt-1 text-xs">
            Who can see {project.name} and what they can do on this board.
          </p>
        </header>
        <BoardMembersPanel wsId={wsId} project={project} />
      </div>
    </div>
  )
}

/** Labels tab — the labels of this board. */
export function BoardSettingsLabelsView() {
  const { wsId, pKey } = useParams({ strict: false }) as { wsId: string; pKey: string }
  const projectQ = useProjectByKey(wsId, pKey)
  const project = projectQ.data

  if (projectQ.isLoading || !project) {
    return (
      <div className="text-surface-500 flex h-full items-center justify-center text-sm">
        Loading board…
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto">
      <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
        <header>
          <h1 className="text-lg font-semibold">Labels</h1>
          <p className="text-surface-500 mt-1 text-xs">
            Labels live on a board. This list shows the labels of {project.name}.
          </p>
        </header>
        <WorkspaceLabelsPanel wsId={wsId} projectId={project.id} />
      </div>
    </div>
  )
}

/** Milestones tab — the milestones of this board. */
export function BoardSettingsMilestonesView() {
  const { wsId, pKey } = useParams({ strict: false }) as { wsId: string; pKey: string }
  const projectQ = useProjectByKey(wsId, pKey)
  const project = projectQ.data

  if (projectQ.isLoading || !project) {
    return (
      <div className="text-surface-500 flex h-full items-center justify-center text-sm">
        Loading board…
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto">
      <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
        <header>
          <h1 className="text-lg font-semibold">Milestones</h1>
          <p className="text-surface-500 mt-1 text-xs">
            Milestones live on a board. This list shows the milestones of {project.name}.
          </p>
        </header>
        <WorkspaceMilestonesPanel wsId={wsId} projectId={project.id} />
      </div>
    </div>
  )
}

export function BoardSettingsView() {
  const { wsId, pKey } = useParams({ strict: false }) as { wsId: string; pKey: string }
  return (
    <NavigationGuardProvider>
      <BoardSettingsScreen wsId={wsId} pKey={pKey} />
    </NavigationGuardProvider>
  )
}

export function BoardSettingsScreen({ wsId, pKey }: { wsId: string; pKey: string }) {
  const { user } = useAuth()
  const projectQ = useProjectByKey(wsId, pKey)
  const project = projectQ.data

  const wsMembers = useWorkspaceMembers(wsId)
  const projectMembers = useQuery({
    queryKey: ['project-members', project?.id],
    queryFn: () => api<ProjectMember[]>(`/api/v1/projects/${project!.id}/members`),
    enabled: !!project,
  })

  const wsRole = wsMembers.data?.find((m) => m.userId === user?.id)?.role
  const projectRole = projectMembers.data?.find((m) => m.userId === user?.id)?.role

  // Mirrors the server policy: an explicit project role wins, otherwise the
  // workspace role decides.
  const canWriteProject = projectRole
    ? projectRole === 'lead' || projectRole === 'contributor'
    : wsRole === 'owner' || wsRole === 'admin' || wsRole === 'member'
  // Board admin = project lead, or a workspace owner.
  const canAdminProject = projectRole === 'lead' || wsRole === 'owner'
  // Moving a board needs the same rights the board view's kebab menu used
  // to require: the board lead, or a workspace owner or admin.
  const canMoveProject = canAdminProject || wsRole === 'admin'

  // The role lists decide whether the board is editable. Wait for them so
  // the screen never flashes the read-only state at an admin.
  const rolesLoading = !!project && (wsMembers.isLoading || projectMembers.isLoading)
  if (projectQ.isLoading || rolesLoading) {
    return (
      <div className="text-surface-500 flex h-full items-center justify-center text-sm">
        Loading board settings…
      </div>
    )
  }

  if (!project) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-red-500">Board not found.</div>
    )
  }

  return (
    <div className="h-full overflow-auto">
      <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
        <header>
          <h1 className="text-lg font-semibold">Board settings</h1>
          <p className="text-surface-500 mt-1 text-xs">
            {project.name} · {project.key}
          </p>
        </header>

        <IdentitySection
          projectId={project.id}
          projectKey={project.key}
          initial={{ name: project.name, color: project.color, icon: project.icon ?? '' }}
          version={project.version}
          canEdit={canWriteProject}
        />

        <ColumnsSection pId={project.id} canEdit={canAdminProject} />

        <MoveSection
          wsId={wsId}
          pKey={pKey}
          project={project}
          canMove={canMoveProject}
        />

        <ArchiveSection wsId={wsId} project={project} canArchive={canAdminProject} />
      </div>
    </div>
  )
}

/**
 * Move the board to another workspace.
 *
 * The action used to live in the board view's kebab menu. It lives here now,
 * next to the other board settings, so the board's own settings screen is the
 * single place that owns its actions. The preview, the conflict list and the
 * confirmation come from the shared transfer service.
 */
function MoveSection({
  wsId,
  pKey,
  project,
  canMove,
}: {
  wsId: string
  pKey: string
  project: Project
  canMove: boolean
}) {
  const navigate = useNavigate()
  const [moving, setMoving] = useState(false)

  return (
    <section className="card p-4" data-testid="board-move" aria-label="Move board">
      <h2 className="text-sm font-semibold">Move board</h2>
      <p className="text-surface-600 dark:text-surface-300 mt-1 text-xs">
        Move {project.name} and everything on it to another workspace you are a member of. The
        destination has to be empty of boards with the same key, and members who would lose
        access are listed before anything moves.
      </p>
      {canMove ? (
        <button
          type="button"
          className="btn-secondary mt-3 min-h-11 px-3 py-1.5 text-sm font-medium"
          onClick={() => setMoving(true)}
        >
          Move to workspace…
        </button>
      ) : (
        <p className="text-surface-500 mt-3 text-xs">
          Only board leads and workspace admins can move this board.
        </p>
      )}

      {moving && (
        <MoveToWorkspaceDialog
          kind="project"
          id={project.id}
          label={project.name}
          sourceWorkspaceId={wsId}
          onClose={() => setMoving(false)}
          onMoved={(result) => {
            setMoving(false)
            toast(`Moved "${project.name}"`, { kind: 'success' })
            navigate({
              to: '/w/$wsId/p/$pKey',
              params: { wsId: result.targetWorkspaceId, pKey: project.key },
            })
          }}
        />
      )}
    </section>
  )
}

/**
 * C. Danger area — archive the board.
 *
 * The action used to live in the board view's kebab menu. It lives here now
 * so the confirmation can name the board, so the board's own settings screen
 * is the single place that owns its destructive actions, and so the restore
 * path is one click away.
 */
function ArchiveSection({
  wsId,
  project,
  canArchive,
}: {
  wsId: string
  project: Project
  canArchive: boolean
}) {
  const [confirming, setConfirming] = useState(false)

  return (
    <section
      className="card border border-red-300 p-4 dark:border-red-900"
      data-testid="board-archive"
      aria-label="Archive board"
    >
      <h2 className="text-sm font-semibold text-red-700 dark:text-red-300">Archive board</h2>
      <p className="text-surface-600 dark:text-surface-300 mt-1 text-xs">
        Archiving {project.name} removes it from the sidebar, the workspace overview and the command palette.
        Its tasks, columns and settings are kept, and you can restore it from{' '}
        <Link to="/me/archive" className="text-brand-600 dark:text-brand-300 underline">
          Archive
        </Link>
        .
      </p>
      {canArchive ? (
        <button
          type="button"
          className="mt-3 min-h-11 rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950"
          onClick={() => setConfirming(true)}
        >
          Archive board…
        </button>
      ) : (
        <p className="text-surface-500 mt-3 text-xs">
          Only board leads and workspace owners can archive this board.
        </p>
      )}
      <ArchiveBoardDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        wsId={wsId}
        project={project}
      />
    </section>
  )
}

/** A. Board identity — display name, colour, icon. */
function IdentitySection({
  projectId,
  projectKey,
  initial,
  version,
  canEdit,
}: {
  projectId: string
  projectKey: string
  initial: Identity
  version: number
  canEdit: boolean
}) {
  const update = useUpdateProjectIdentity(projectId)
  const qc = useQueryClient()
  // The project version the current edit started from. Only re-adopted from
  // the server while clean, so a refetch mid-edit cannot launder it.
  const [baseVersion, setBaseVersion] = useState(version)
  const [conflict, setConflict] = useState<Project | null>(null)
  const [checking, setChecking] = useState(false)
  const [name, setName] = useState(initial.name)
  const [color, setColor] = useState(initial.color)
  const [icon, setIcon] = useState(initial.icon)
  const [baseline, setBaseline] = useState<Identity>(initial)
  const [error, setError] = useState<string | null>(null)

  // The server is the source of truth: adopt its values when they actually
  // change. The baseline always tracks the latest server state so a cancel
  // restores the most recent server view, but the input fields are only
  // re-adopted when the user is NOT mid-edit — otherwise a refetch (realtime
  // push, manual refresh, or a sibling mutation that refetches this project)
  // would silently clobber the value the user is typing.
  const serverSignature = JSON.stringify([initial.name, initial.color, initial.icon, version])
  const lastSignature = useRef<string | null>(null)
  const trimmedName = name.trim()
  const nameError = trimmedName.length === 0 ? 'Board name is required.' : null
  const dirty = trimmedName !== baseline.name || color !== baseline.color || icon !== baseline.icon
  const saving = update.isPending || checking
  useEffect(() => {
    if (lastSignature.current === serverSignature) return
    lastSignature.current = serverSignature
    setBaseline(initial)
    if (!dirty) {
      setName(initial.name)
      setColor(initial.color)
      setIcon(initial.icon)
      setBaseVersion(version)
    }
  }, [serverSignature, initial, dirty, version])

  // Same guard as the columns editor: a half-typed board name must not be
  // lost to an accidental navigation. Disabled while the PATCH is in flight
  // so the user can neither start a new edit nor trigger the SPA prompt
  // mid-save — the issue is half-edit + half-navigation is unrecoverable.
  useUnsavedChangesGuard(dirty && !saving)

  async function save() {
    if (!dirty || nameError) return
    setError(null)
    setConflict(null)
    setChecking(true)
    try {
      // Check first: if the board moved on since this edit started, keep the
      // draft and let the user decide. The PATCH below also quotes the base
      // version, so a write landing between this GET and the PATCH is a 409
      // on the server, never a silent overwrite.
      const fresh = await api<Project>(`/api/v1/projects/${projectId}`)
      if (fresh.version !== baseVersion) {
        setConflict(fresh)
        void qc.invalidateQueries({ queryKey: ['project-by-key'] })
        return
      }
      // Only the changed keys are sent — the board key is immutable and the
      // server rejects unknown fields.
      const patch: ProjectIdentityPatch = {}
      if (trimmedName !== baseline.name) patch.name = trimmedName
      if (color !== baseline.color) patch.color = color
      if (icon !== baseline.icon) patch.icon = icon
      if (Object.keys(patch).length === 0) return
      const saved = await update.mutateAsync({ patch, version: baseVersion })
      setBaseline({ name: saved.name, color: saved.color, icon: saved.icon ?? '' })
      setBaseVersion(saved.version)
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // Lost the race after the check above: same keep-the-draft choice.
        try {
          setConflict(await api<Project>(`/api/v1/projects/${projectId}`))
          void qc.invalidateQueries({ queryKey: ['project-by-key'] })
          return
        } catch {
          // Fall through to the generic error when the refetch fails too.
        }
      }
      setError(e instanceof ApiError ? e.message : 'Could not update the board.')
    } finally {
      setChecking(false)
    }
  }

  function adopt(server: Project, keepDraft: boolean) {
    const next = { name: server.name, color: server.color, icon: server.icon ?? '' }
    setBaseline(next)
    setBaseVersion(server.version)
    if (!keepDraft) {
      setName(next.name)
      setColor(next.color)
      setIcon(next.icon)
    }
    setConflict(null)
  }

  function cancel() {
    setName(baseline.name)
    setColor(baseline.color)
    setIcon(baseline.icon)
    setError(null)
    setConflict(null)
  }

  return (
    <section className="card p-4" data-testid="board-identity" aria-label="Board identity">
      <h2 className="text-sm font-semibold">Board identity</h2>
      {!canEdit && (
        <p className="text-surface-500 mt-1 text-xs">
          Only board leads and contributors can change this board.
        </p>
      )}

      <div className="mt-3 space-y-3">
        <label className="block">
          <span className="text-surface-600 dark:text-surface-300 block text-xs font-medium">
            Display name
          </span>
          <input
            className="input mt-1"
            value={name}
            maxLength={120}
            disabled={!canEdit || saving}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        {nameError && <p className="text-xs text-red-600 dark:text-red-300">{nameError}</p>}

        <div>
          <span className="text-surface-600 dark:text-surface-300 block text-xs font-medium">Colour</span>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <input
              type="color"
              aria-label="Colour"
              className="border-surface-300 dark:border-surface-700 h-9 w-12 cursor-pointer rounded border"
              value={color}
              disabled={!canEdit || saving}
              onChange={(e) => setColor(e.target.value)}
            />
            {PRESET_COLORS.map((preset) => (
              <button
                key={preset}
                type="button"
                className="border-surface-300 dark:border-surface-700 min-h-11 min-w-11 rounded-md border"
                style={{ backgroundColor: preset }}
                aria-label={`Use colour ${preset}`}
                title={preset}
                disabled={!canEdit || saving}
                onClick={() => setColor(preset)}
              />
            ))}
            <button
              type="button"
              className="btn-ghost min-h-11"
              disabled={!canEdit || saving}
              onClick={() => setColor(DEFAULT_BOARD_COLOR)}
            >
              Reset colour
            </button>
          </div>
        </div>

        <div>
          <span className="text-surface-600 dark:text-surface-300 block text-xs font-medium">Icon</span>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <input
              className="input max-w-[12rem]"
              value={icon}
              maxLength={8}
              aria-label="Icon"
              placeholder="Emoji or short label"
              disabled={!canEdit || saving}
              onChange={(e) => setIcon(e.target.value)}
            />
            <button
              type="button"
              className="btn-ghost min-h-11"
              disabled={!canEdit || saving}
              onClick={() => setIcon('')}
            >
              Clear icon
            </button>
          </div>
        </div>

        <div>
          <span className="text-surface-600 dark:text-surface-300 block text-xs font-medium">Board key</span>
          <code
            data-testid="board-key-readonly"
            className="bg-surface-200 dark:bg-surface-800 mt-1 inline-block rounded px-2 py-1 font-mono text-xs"
          >
            {projectKey}
          </code>
          <p className="text-surface-500 mt-1 text-xs">
            Board keys are immutable — they are part of every task key, so they cannot be edited.
          </p>
        </div>
      </div>

      <p aria-live="polite" className="text-surface-500 mt-3 text-xs">
        {dirty ? 'Unsaved changes' : 'No pending changes'}
      </p>

      {error && (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-300">
          {error}
        </p>
      )}

      {conflict && (
        <div role="alert" className="mt-2 rounded-md border border-red-300 p-3 text-sm dark:border-red-800">
          <p className="text-red-600 dark:text-red-300">
            This board was changed by someone else since you started editing.
          </p>
          <p className="text-surface-600 dark:text-surface-300 mt-1 break-words">
            Latest saved: {conflict.name} · colour {conflict.color} · icon {conflict.icon || 'none'}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className="btn-ghost min-h-11" onClick={() => adopt(conflict, false)}>
              Discard my changes
            </button>
            <button type="button" className="btn-ghost min-h-11" onClick={() => adopt(conflict, true)}>
              Keep my changes
            </button>
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {canEdit && (
          <button
            type="button"
            className="btn-primary min-h-11"
            disabled={!dirty || saving || !!nameError}
            onClick={save}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
        <button
          type="button"
          className="btn-ghost min-h-11"
          disabled={saving || !dirty}
          onClick={cancel}
        >
          Cancel
        </button>
      </div>
    </section>
  )
}

/** B. Board columns — inherit from the workspace, override it, or reset. */
function ColumnsSection({ pId, canEdit }: { pId: string; canEdit: boolean }) {
  const defaults = useBoardListDefaults(pId)
  const save = useSaveBoardListDefaults(pId)
  // The board tier's own row — null means the board inherits.
  const ownFields = defaults.data?.fields ?? null
  // What the board inherits when it has no row of its own.
  const workspaceFields = defaults.data?.layers.workspace.fields ?? null
  const inherited = workspaceFields ?? DEFAULT_LIST_FIELDS
  const source = workspaceFields ? 'workspace' : 'application'

  // Same edit session as the workspace tier: Save/Reset quote the version the
  // draft started from, and a 409 keeps the draft.
  const draft = useListDefaultsDraft(
    defaults.data,
    defaults.refetch,
    save,
    workspaceFields ? "inherits this workspace's default" : 'inherits the application default',
  )

  if (defaults.isLoading) {
    return (
      <div className="card text-surface-500 p-4 text-sm" data-testid="board-columns">
        Loading board columns…
      </div>
    )
  }

  return (
    <div data-testid="board-columns">
      <ListFieldsEditor
        label="Board columns"
        subject="board"
        value={ownFields}
        pending={draft.pending}
        inheritedValue={inherited}
        source={source}
        canEdit={canEdit}
        readOnlyReason="Only board leads and workspace owners can change these columns."
        saving={save.isPending}
        error={draft.error}
        dirty={draft.dirty}
        conflict={draft.conflict}
        notice={draft.staleNote}
        onChange={draft.onChange}
        onSave={() => void draft.save('Could not save the board columns.')}
        onCancel={draft.cancel}
        onResetToInherit={() => void draft.reset('Could not reset the board columns.')}
      />
    </div>
  )
}
